import type { PropType } from "vue";
import type { GlobalOptionsSelect } from "@/components/helpers/types";
import {
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuItemIndicator,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "reka-ui";
import { defineComponent } from "vue";
import { forwardEmit } from "@/utils";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import Icon from "../../ui/icon/Icon";

const Root = defineComponent({
  name: "Dropdown",
  emits: {
    "update:open": (_value: boolean) => true,
  },
  props: {
    defaultOpen: p.boolean(),
    dir: p.string<"ltr" | "rtl">("ltr"),
    open: {
      type: Boolean as PropType<boolean | undefined>,
      default: undefined,
    },
    modal: p.boolean(),
  },
  setup(props, { slots, emit }) {
    return () => (
      <DropdownMenuRoot
        defaultOpen={props.defaultOpen}
        dir={props.dir}
        open={props.open}
        modal={props.modal}
        onUpdate:open={(val: boolean) => emit("update:open", val)}
      >
        {slots.default?.()}
      </DropdownMenuRoot>
    );
  },
});

const Trigger = defineComponent({
  name: "DropdownTrigger",
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
  },
  setup(props, { slots, attrs }) {
    return () => (
      <DropdownMenuTrigger
        asChild={props.asChild}
        as={props.as}
        class={cn("outline-hidden", attrs.class ?? "")}
      >
        {slots.default?.()}
      </DropdownMenuTrigger>
    );
  },
});

const Content = defineComponent({
  name: "DropdownContent",
  emits: {
    escapeKeyDown: (_event: KeyboardEvent) => true,
    pointerDownOutside: (_event: Event) => true,
    focusOutside: (_event: Event) => true,
    interactOutside: (_event: Event) => true,
    dismiss: () => true,
    entryFocus: (_event: Event) => true,
    openAutoFocus: (_event: Event) => true,
    closeAutoFocus: (_event: Event) => true,
  },
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
    sideOffset: p.number(4),
    side: p.string<"bottom" | "top" | "right" | "left">("bottom"),
    align: p.string<"end" | "start" | "center">("end"),
    alignOffset: p.number(0),
    avoidCollisions: p.boolean(true),
    collisionBoundary: p.custom<Element | (Element | null)[] | null>([]),
    collisionPadding: p.number(4),
    arrowPadding: p.number(4),
    hideWhenDetached: p.boolean(false),
    updatePositionStrategy: p.string<"always" | "optimized">("optimized"),
    onPlaced: p.func<() => void>(),
    prioritizePosition: p.boolean(false),
    loop: p.boolean(false),
  },
  setup(props, { slots, attrs }) {
    return () => (
      <DropdownMenuPortal>
        <DropdownMenuContent
          loop={props.loop}
          prioritizePosition={props.prioritizePosition}
          asChild={props.asChild}
          as={props.as}
          side={props.side}
          align={props.align}
          sideOffset={props.sideOffset}
          alignOffset={props.alignOffset}
          updatePositionStrategy={props.updatePositionStrategy}
          avoidCollisions={props.avoidCollisions}
          collisionBoundary={props.collisionBoundary}
          collisionPadding={props.collisionPadding}
          arrowPadding={props.arrowPadding}
          class={[
            cn(
              "z-80 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
              attrs.class ?? ""
            ),
          ]}
        >
          {slots.default?.()}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    );
  },
});

const Label = defineComponent({
  name: "DropdownLabel",
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
  },
  setup(props, { slots, attrs }) {
    return () => (
      <DropdownMenuLabel
        asChild={props.asChild}
        as={props.as}
        class={cn("px-2 py-1.5 text-xs font-medium text-muted-foreground", attrs.class ?? "")}
      >
        {slots.default?.()}
      </DropdownMenuLabel>
    );
  },
});

const Group = defineComponent({
  name: "DropdownGroup",
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
    heading: p.string(""),
  },
  setup(props, { slots, attrs }) {
    return () => (
      <DropdownMenuGroup asChild={props.asChild} as={props.as} class={attrs.class ?? ""}>
        {props.heading && <Label>{props.heading}</Label>}
        {slots.default?.()}
      </DropdownMenuGroup>
    );
  },
});

const Item = defineComponent({
  name: "DropdownItem",
  emits: {
    select: (_value: GlobalOptionsSelect) => true,
  },
  props: {
    value: {
      type: [String, Object] as PropType<GlobalOptionsSelect>,
    },
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
    disabled: p.boolean(false),
    active: p.boolean(false),
    inset: p.boolean(false),
    class: p.string(""),
  },
  setup(props, { slots, emit }) {
    return () => (
      <DropdownMenuItem
        asChild={props.asChild}
        as={props.as}
        disabled={props.disabled}
        onSelect={() => emit("select", props.value ?? "")}
        class={cn(
          "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors focus:bg-muted focus:text-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
          props.inset && "pl-8",
          props.class
        )}
      >
        {slots.default?.()}
      </DropdownMenuItem>
    );
  },
});

const Separator = defineComponent({
  name: "DropdownSeparator",
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
  },
  setup(props, { attrs }) {
    return () => (
      <DropdownMenuSeparator
        asChild={props.asChild}
        as={props.as}
        class={cn("-mx-1 my-1 h-px bg-muted", attrs.class ?? "")}
      />
    );
  },
});

const ShortCut = defineComponent({
  name: "DropdownShortCut",
  props: {
    class: p.string(""),
  },
  setup(props, { slots }) {
    return () => (
      <span class={cn("ml-auto text-xs tracking-widest opacity-60", props.class)}>
        {slots.default?.()}
      </span>
    );
  },
});

const Sub = defineComponent({
  name: "DropdownSub",
  setup(_, { slots }) {
    return () => <DropdownMenuSub>{slots.default?.()}</DropdownMenuSub>;
  },
});

const SubTrigger = defineComponent({
  name: "DropdownSubTrigger",
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
    disabled: p.boolean(false),
    textValue: p.string(),
    value: p.string(() => {
      return Math.random().toString(36).substring(7);
    }),
  },
  setup(props, { slots, attrs }) {
    return () => (
      <DropdownMenuSubTrigger
        asChild={props.asChild}
        as={props.as}
        disabled={props.disabled}
        textValue={props.textValue}
        class={cn(
          "flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden focus:bg-muted data-[state=open]:bg-muted",
          attrs.class ?? ""
        )}
      >
        {slots.default?.()}
        <Icon icon="lucide:chevron-right" class="ml-auto h-3.5 w-3.5" />
      </DropdownMenuSubTrigger>
    );
  },
});

const SubContent = defineComponent({
  name: "DropdownSubContent",
  emits: {
    escapeKeyDown: (_event: KeyboardEvent) => true,
    pointerDownOutside: (_event: Event) => true,
    focusOutside: (_event: Event) => true,
    interactOutside: (_event: Event) => true,
    dismiss: () => true,
    entryFocus: (_event: Event) => true,
    openAutoFocus: (_event: Event) => true,
    closeAutoFocus: (_event: Event) => true,
  },
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
    sideOffset: p.number(4),
    side: p.string<"bottom" | "top">("bottom"),
    align: p.string<"end" | "start">("end"),
    alignOffset: p.number(0),
    avoidCollisions: p.boolean(true),
    collisionBoundary: p.custom<Element | (Element | null)[] | null>([]),
    collisionPadding: p.number(4),
    arrowPadding: p.number(4),
    hideWhenDetached: p.boolean(false),
    updatePositionStrategy: p.string<"always" | "optimized">("optimized"),
    onPlaced: p.func<() => void>(),
    prioritizePosition: p.boolean(false),
    loop: p.boolean(false),
  },
  setup(props, { slots, attrs, emit }) {
    const emits = forwardEmit(emit);
    return () => (
      <DropdownMenuSubContent
        {...emits}
        loop={props.loop}
        prioritizePosition={props.prioritizePosition}
        asChild={props.asChild}
        as={props.as}
        // side={props.side}
        // align={props.align}
        sideOffset={props.sideOffset}
        alignOffset={props.alignOffset}
        updatePositionStrategy={props.updatePositionStrategy}
        avoidCollisions={props.avoidCollisions}
        collisionBoundary={props.collisionBoundary}
        collisionPadding={props.collisionPadding}
        arrowPadding={props.arrowPadding}
        class={cn(
          "z-80 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          attrs.class ?? ""
        )}
      >
        {slots.default?.()}
      </DropdownMenuSubContent>
    );
  },
});

const CheckboxItem = defineComponent({
  name: "DropdownCheckboxItem",
  emits: {
    "update:checked": (_value: boolean) => true,
    select: (_e: Event) => true,
  },
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(false),
    disabled: p.boolean(false),
    checked: p.boolean(false),
  },
  setup(props, { slots, attrs, emit }) {
    return () => (
      <DropdownMenuCheckboxItem
        as={props.as}
        disabled={props.disabled}
        asChild={props.asChild}
        modelValue={props.checked}
        onUpdate:modelValue={(val: boolean) => emit("update:checked", val)}
        onSelect={(e: Event) => emit("select", e)}
        class={cn(
          "focus:bg-accent focus:text-accent-foreground relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-hidden transition-colors data-disabled:pointer-events-none data-disabled:opacity-50",
          attrs.class ?? ""
        )}
      >
        <span class="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <DropdownMenuItemIndicator>
            <Icon icon="lucide:check" class="h-4 w-4" />
          </DropdownMenuItemIndicator>
        </span>
        {slots.default?.()}
      </DropdownMenuCheckboxItem>
    );
  },
});

const RadioGroup = defineComponent({
  name: "DropdownRadioGroup",
  emits: {
    "update:modelValue": (_value: string) => true,
  },
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(false),
    value: p.string(),
  },
  setup(props, { slots, emit }) {
    return () => (
      <DropdownMenuRadioGroup
        as={props.as}
        asChild={props.asChild}
        modelValue={props.value}
        onUpdate:modelValue={(e: string) => emit("update:modelValue", e)}
        class="flex flex-col gap-1"
      >
        {slots.default?.()}
      </DropdownMenuRadioGroup>
    );
  },
});

const RadioItem = defineComponent({
  name: "DropdownRadioItem",
  emits: {
    select: (_e: Event) => true,
  },
  props: {
    as: p.string<keyof HTMLElementTagNameMap>(),
    asChild: p.boolean(false),
    disabled: p.boolean(false),
    value: p.$string(),
    textValue: p.string(),
  },
  setup(props, { slots, attrs, emit }) {
    return () => (
      <DropdownMenuRadioItem
        value={props.value}
        as={props.as}
        asChild={props.asChild}
        disabled={props.disabled}
        onSelect={(e: Event) => emit("select", e)}
        textValue={props.textValue}
        class={cn(
          "relative flex cursor-default select-none items-center rounded-md py-1.5 pl-7 text-sm outline-hidden transition-colors data-disabled:pointer-events-none data-highlighted:bg-muted data-disabled:opacity-50",
          attrs.class ?? ""
        )}
      >
        <span class="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <DropdownMenuItemIndicator>
            <Icon icon="radix-icons:dot-filled" class="h-4 w-4 fill-current" />
          </DropdownMenuItemIndicator>
        </span>
        {slots.default?.()}
      </DropdownMenuRadioItem>
    );
  },
});

const Shortcut = defineComponent({
  name: "DropdownShortcut",

  setup(props, { slots, attrs }) {
    return () => (
      <span class={cn("ml-auto text-xs tracking-widest opacity-60", attrs.class ?? "")}>
        {slots.default?.()}
      </span>
    );
  },
});

const Portal = DropdownMenuPortal;

export default {
  Root,
  Trigger,
  Content,
  Group,
  Item,
  Separator,
  ShortCut,
  Sub,
  SubTrigger,
  SubContent,
  CheckboxItem,
  RadioGroup,
  RadioItem,
  Portal,
  Label,
  Shortcut,
};
